/**
 * API client with auth + response normalization
 *
 * DEFAULT_API_BASE_URL is intentionally a relative path ("/api") so that
 * production builds always call the same origin (no CORS). For local
 * development with a separate frontend server, set REACT_APP_API_BASE_URL
 * in frontend/.env (e.g. http://127.0.0.1:8000/api).
 */

const DEFAULT_API_BASE_URL = "/api";

export const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || DEFAULT_API_BASE_URL;

// When API is relative (e.g. /api), derive WebSocket from current origin
function getWsBaseUrl() {
  if (process.env.REACT_APP_WS_BASE_URL) {
    return process.env.REACT_APP_WS_BASE_URL;
  }
  if (typeof API_BASE_URL === "string" && API_BASE_URL.startsWith("/")) {
    if (typeof window !== "undefined") {
      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      return `${protocol}//${window.location.host}`;
    }
    return "ws://127.0.0.1:8000";
  }
  return API_BASE_URL.replace(/\/api\/?$/, "").replace(/^http/, "ws");
}
export const WS_BASE_URL = getWsBaseUrl();

const getAccessToken = () => localStorage.getItem("access_token");
const getRefreshToken = () => localStorage.getItem("refresh_token");

const getLanguage = () => {
  try {
    return localStorage.getItem("dstl_lang") || "en";
  } catch (e) {
    return "en";
  }
};

/** Get CSRF token from cookie (same-origin); required for Django POST/PUT/PATCH/DELETE */
function getCsrfToken() {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(/\bcsrftoken=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

const setAccessToken = (token) => {
  if (token) {
    localStorage.setItem("access_token", token);
  }
};

const clearAuthTokens = () => {
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
};

const normalizeResponse = (payload) => {
  if (payload && typeof payload === "object" && "success" in payload) {
    // If the response has pagination metadata, preserve the full structure
    if (payload.pagination) {
      return payload;
    }
    // Otherwise, return just the data if it exists
    return payload.data !== undefined ? payload.data : payload;
  }
  return payload;
};

const buildError = async (response) => {
  let payload = null;
  try {
    payload = await response.json();
  } catch (error) {
    payload = null;
  }

  const message =
    payload?.message ||
    payload?.error ||
    payload?.detail ||
    response.statusText ||
    "Request failed";

  const error = new Error(message);
  error.status = response.status;
  error.details = payload?.details || payload;
  return error;
};

const refreshAccessToken = async () => {
  const refresh = getRefreshToken();
  if (!refresh) return null;

  const headers = { "Content-Type": "application/json" };
  const csrf = getCsrfToken();
  if (csrf) headers["X-CSRFToken"] = csrf;
  const response = await fetch(`${API_BASE_URL}/auth/refresh/`, {
    method: "POST",
    headers,
    body: JSON.stringify({ refresh }),
  });

  if (!response.ok) {
    clearAuthTokens();
    return null;
  }

  const payload = await response.json();
  if (payload?.access) {
    setAccessToken(payload.access);
    return payload.access;
  }

  clearAuthTokens();
  return null;
};

const withAuthHeader = (headers = {}) => {
  const token = getAccessToken();
  if (!token) return headers;
  return {
    ...headers,
    Authorization: `Bearer ${token}`,
  };
};

const request = async (path, options = {}, config = {}) => {
  const { requiresAuth = true, retryOnUnauthorized = true, responseType } = config;
  const url = path.startsWith("http") ? path : `${API_BASE_URL}${path}`;

  const headers = { ...options.headers };
  headers["Accept-Language"] = getLanguage();
  if (requiresAuth) Object.assign(headers, withAuthHeader(headers));
  const method = (options.method || "GET").toUpperCase();
  if (["POST", "PUT", "PATCH", "DELETE"].includes(method)) {
    const csrf = getCsrfToken();
    if (csrf) headers["X-CSRFToken"] = csrf;
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (response.status === 204) {
    return null;
  }

  if (response.ok) {
    if (responseType === "blob") {
      return response.blob();
    }
    const payload = await response.json();
    return normalizeResponse(payload);
  }

  if (response.status === 401 && requiresAuth && retryOnUnauthorized) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      return request(path, options, {
        requiresAuth,
        retryOnUnauthorized: false,
      });
    }
  }

  throw await buildError(response);
};

const jsonRequest = (method, path, body, config = {}) =>
  request(
    path,
    {
      method,
      headers: {
        "Content-Type": "application/json",
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    },
    config,
  );

export const apiClient = {
  request,
  get: (path, config) => request(path, {}, config),
  getBlob: (path, config) => request(path, {}, { ...config, responseType: "blob" }),
  post: (path, body, config) => jsonRequest("POST", path, body, config),
  put: (path, body, config) => jsonRequest("PUT", path, body, config),
  patch: (path, body, config) => jsonRequest("PATCH", path, body, config),
  delete: (path, config) => request(path, { method: "DELETE" }, config),
  clearAuthTokens,
  setAccessToken,
};
